import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { galleryCategories, galleryImages, type GalleryCategory, type GalleryImage } from "../config/gallery";
import { useLanguage } from "../i18n/LanguageContext";
import { SmartImage } from "./SmartImage";

const aspectClass = { landscape: "aspect-[4/3]", portrait: "aspect-[3/4] sm:row-span-2 sm:aspect-auto", square: "aspect-square" };

export const Gallery = () => {
  const { t, tr } = useLanguage();
  const [filter, setFilter] = useState<GalleryCategory | "alle">("alle");
  const [active, setActive] = useState<number | null>(null);
  const touchX = useRef<number | null>(null);

  const visible = galleryImages.filter((g) => filter === "alle" || g.category === filter);
  const openable = visible.filter((g) => g.src);

  const step = useCallback((dir: 1 | -1) => setActive((a) => (a === null ? a : (a + dir + openable.length) % openable.length)), [openable.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setActive(null); if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [active, step]);

  const current: GalleryImage | null = active !== null ? openable[active] : null;

  return (
    <div data-testid="gallery">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Kategorien" data-testid="gallery-filters">
        {galleryCategories.map((c) => (
          <button key={c.id} type="button" role="tab" aria-selected={filter === c.id} onClick={() => setFilter(c.id)} data-testid={`gallery-filter-${c.id}`} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${filter === c.id ? "bg-charcoal text-cream" : "bg-sand-50 text-charcoal-700 hover:bg-sand-100"}`}>
            {tr(c.label)}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="mt-10 text-charcoal-500" data-testid="gallery-empty">{t.galleryPage.empty}</p>
      ) : (
        <motion.ul layout className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:auto-rows-[220px] lg:auto-rows-[260px]" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((img) => {
              const idx = openable.indexOf(img);
              return (
                <motion.li key={img.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.35 }} className={`${img.aspect === "portrait" ? "sm:row-span-2" : ""}`}>
                  <button type="button" disabled={!img.src} onClick={() => idx >= 0 && setActive(idx)} className={`group relative block h-full w-full overflow-hidden rounded-3xl text-left ${aspectClass[img.aspect ?? "landscape"]} sm:aspect-auto sm:h-full disabled:cursor-default`} aria-label={tr(img.caption)} data-testid={`gallery-item-${img.id}`}>
                    <SmartImage src={img.src} alt={tr(img.alt)} isPlaceholder={img.isPlaceholder} className="h-full w-full" imgClassName="transition-transform duration-700 ease-out group-hover:scale-[1.05]" />
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/70 to-transparent p-4 pt-10 text-sm font-medium text-cream">{tr(img.caption)}</span>
                  </button>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </motion.ul>
      )}

      <AnimatePresence>
        {current && (
          <motion.div role="dialog" aria-modal="true" aria-label={tr(current.caption)} className="fixed inset-0 z-[70] flex items-center justify-center bg-charcoal/95 p-4 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)} data-testid="lightbox"
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => { if (touchX.current === null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1); touchX.current = null; }}>
            <button type="button" className="absolute right-4 top-4 rounded-full bg-cream/10 p-3 text-cream transition-colors hover:bg-cream/20" onClick={() => setActive(null)} aria-label={t.galleryPage.lightboxClose} data-testid="lightbox-close"><X className="h-5 w-5" /></button>
            {openable.length > 1 && (
              <>
                <button type="button" className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-cream/10 p-3 text-cream transition-colors hover:bg-cream/20 md:left-6" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label={t.galleryPage.prev} data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-cream/10 p-3 text-cream transition-colors hover:bg-cream/20 md:right-6" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label={t.galleryPage.next} data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></button>
              </>
            )}
            <motion.figure key={current.id} className="max-h-full max-w-5xl" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} onClick={(e) => e.stopPropagation()}>
              <img src={current.src} alt={tr(current.alt)} className="max-h-[80vh] w-auto rounded-2xl object-contain" data-testid="lightbox-image" />
              <figcaption className="mt-4 flex items-center justify-between text-sm text-cream/80">
                <span data-testid="lightbox-caption">{tr(current.caption)}</span>
                <span>{(active ?? 0) + 1} / {openable.length}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
